/** Reconnect backoff (SPEC §4.1): 1, 2, 4, 8 s, then every 10 s. */
export function backoffDelayMs(attempt: number): number {
  const schedule = [1000, 2000, 4000, 8000];
  return schedule[attempt] ?? 10_000;
}

export type ReconnectState = 'idle' | 'waiting' | 'connecting' | 'stopped';

/**
 * Retries `connect` with backoff until it succeeds or `stop()` is called.
 * The ride keeps running meanwhile; the caller re-applies mode/target on success.
 */
export class Reconnector {
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stateValue: ReconnectState = 'idle';

  constructor(
    private readonly connect: () => Promise<void>,
    private readonly hooks: {
      onAttempt?: (attempt: number, delayMs: number) => void;
      onSuccess?: () => void;
      onFailure?: (err: unknown, attempt: number) => void;
    } = {},
  ) {}

  get state(): ReconnectState {
    return this.stateValue;
  }

  get attempts(): number {
    return this.attempt;
  }

  start(): void {
    if (this.stateValue === 'waiting' || this.stateValue === 'connecting') return;
    this.attempt = 0;
    this.schedule();
  }

  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.stateValue = 'stopped';
  }

  private schedule(): void {
    const delay = backoffDelayMs(this.attempt);
    this.stateValue = 'waiting';
    this.hooks.onAttempt?.(this.attempt, delay);
    this.timer = setTimeout(() => void this.tryOnce(), delay);
  }

  private async tryOnce(): Promise<void> {
    if (this.stateValue === 'stopped') return;
    this.stateValue = 'connecting';
    try {
      await this.connect();
      if ((this.stateValue as ReconnectState) === 'stopped') return;
      this.stateValue = 'idle';
      this.attempt = 0;
      this.hooks.onSuccess?.();
    } catch (err) {
      if ((this.stateValue as ReconnectState) === 'stopped') return;
      this.hooks.onFailure?.(err, this.attempt);
      this.attempt++;
      this.schedule();
    }
  }
}
